import { AccordionRootProps, useAccordionItemContext } from '@chakra-ui/react';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { useMemo, useState } from 'react';
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useGetAccountById } from '../../../../api/accountsService';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
  MFlex,
  MFormField,
  ResolvedTermsPreview,
  TemplateExpressionHelp,
  TermsPreviewToggle,
} from '../../../../components/Monetize';
import MEditor from '../../../../components/Monetize/MEditor';
import { TemplateExpressionWarning } from '../../../../components/Monetize/TemplateExpressionWarning';
import {
  useAccordionControl,
  useExperienceConfiguration,
} from '../../../../hooks';
import { useTermsPreview } from '../../../../hooks/useTermsPreview';
import { useAuth } from '../../../../services/auth0';
import { useQuoteContext } from '../../Quote/quoteContext';

export type QuoteTermsProps = AccordionRootProps;

interface QuoteTermsBodyProps {
  isReadOnly: boolean;
  control: ReturnType<typeof useFormContext>['control'];
  errors: ReturnType<typeof useFormState>['errors'];
  quote: ReturnType<typeof useQuoteContext>['quoteData']['quote'];
  showResolvedPreview: boolean;
  setShowResolvedPreview: (value: boolean) => void;
  handleSubmitButtonWithoutDirtyCheck: ReturnType<
    typeof useQuoteContext
  >['handleSubmitButtonWithoutDirtyCheck'];
  resolvedTerms?: string[] | null;
  isPreviewLoading: boolean;
  previewError: unknown;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const QuoteTermsBody = ({
  isReadOnly,
  control,
  errors,
  quote,
  showResolvedPreview,
  setShowResolvedPreview,
  handleSubmitButtonWithoutDirtyCheck,
  resolvedTerms,
  isPreviewLoading,
  previewError,
}: QuoteTermsBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label="Terms and Conditions"
      />

      <MAccordionPanel p="4" m="0">
        <MFlex justifyContent="flex-end" alignItems="center" mb={2}>
          <TermsPreviewToggle
            isChecked={showResolvedPreview}
            onChange={setShowResolvedPreview}
            testId="contract-terms-preview-toggle"
          />
        </MFlex>
        <MFormField isDisabled={!quote} error={errors.contractTerms}>
          <Controller
            name="contractTerms"
            control={control}
            render={({ field: { onChange, onBlur, ...fields } }) => (
              <>
                <MEditor
                  autoScroll
                  disabled={isReadOnly}
                  handleEditorChange={onChange}
                  onBlur={() => {
                    handleSubmitButtonWithoutDirtyCheck();
                    onBlur();
                  }}
                  boxProps={{
                    pb: 0,
                    minH: 200,
                    w: '100%',
                  }}
                  {...fields}
                />
                <TemplateExpressionWarning value={fields.value} />
                <TemplateExpressionHelp testId="contract-terms-help-btn" />
              </>
            )}
          />
        </MFormField>
        {showResolvedPreview && (
          <ResolvedTermsPreview
            label="Preview with quote data"
            html={resolvedTerms?.[0] ?? ''}
            isLoading={isPreviewLoading}
            errorMessage={
              previewError
                ? 'Could not generate the resolved preview.'
                : undefined
            }
            testId="contract-terms-preview-content"
            textColor="tPurple.base"
          />
        )}
      </MAccordionPanel>
    </>
  );
};

export const QuoteTerms = ({ ...rest }: QuoteTermsProps) => {
  const {
    quoteData: { quote },
    handleSubmitButtonWithoutDirtyCheck,
    isReadOnly,
  } = useQuoteContext();
  const { showTOCPermission } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { currentTenantUserHasRole } = useAuth();

  const canShowTOC = useMemo(() => {
    if (!showTOCPermission) {
      return true;
    }

    if (
      showTOCPermission?.disableQuoteTermsAndConditions === 'SPECIFIC_ROLES'
    ) {
      return currentTenantUserHasRole(showTOCPermission?.allowedRoles || []);
    } else {
      return !showTOCPermission?.disableQuoteTermsAndConditions;
    }
  }, [showTOCPermission, currentTenantUserHasRole]);

  const { accordionValue, handleAccordionChange } = useAccordionControl({
    defaultIsExpanded: true,
  });

  const { control, getValues } = useFormContext();
  const { errors } = useFormState({
    control,
  });

  const [showResolvedPreview, setShowResolvedPreview] = useState(false);
  // Only subscribe to the editor field while the preview is on — otherwise
  // every keystroke re-renders this whole panel for a value nothing reads.
  // The watched value itself is unused: useWatch doesn't replay the field's
  // current value when `disabled` flips back to false, so the live value is
  // always read directly via getValues below instead.
  useWatch({
    name: 'contractTerms',
    control,
    disabled: !showResolvedPreview,
  });
  const { data: accountData, isLoading: isAccountLoading } = useGetAccountById(
    quote?.accountId ?? '',
    {
      enabled: showResolvedPreview && !!quote?.accountId,
      refetchOnWindowFocus: false,
    },
  );
  const liveContractTerms = getValues('contractTerms');
  const previewTermsInput = useMemo(
    () => [liveContractTerms ?? ''],
    [liveContractTerms],
  );
  const {
    resolvedTerms,
    isLoading: isPreviewLoading,
    error: previewError,
  } = useTermsPreview({
    quote,
    account: accountData,
    terms: previewTermsInput,
    enabled: showResolvedPreview && !!quote && !!liveContractTerms,
    isAccountLoading,
    // Contract terms are not list-item wrapped in the rendered PDF
    wrapListItems: false,
  });

  if (!canShowTOC) {
    return null;
  }

  return (
    <MAccordion
      multiple
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
      {...rest}
    >
      <MAccordionItem value="0" borderColor="tGray.lightGray">
        <QuoteTermsBody
          isReadOnly={isReadOnly}
          control={control}
          errors={errors}
          quote={quote}
          showResolvedPreview={showResolvedPreview}
          setShowResolvedPreview={setShowResolvedPreview}
          handleSubmitButtonWithoutDirtyCheck={
            handleSubmitButtonWithoutDirtyCheck
          }
          resolvedTerms={resolvedTerms}
          isPreviewLoading={isPreviewLoading}
          previewError={previewError}
        />
      </MAccordionItem>
    </MAccordion>
  );
};
